import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  updateEventSchema,
  type UpdateEventInput,
} from '@wingseat/shared';
import { useAuth } from '../auth/auth-context';
import {
  useDeleteEvent,
  useEvent,
  useUpdateEvent,
} from '../hooks/use-events';
import { ApiError } from '../lib/api-client';
import {
  formatDate,
  formatDateTime,
  fromDateTimeLocal,
  toDateTimeLocal,
} from '../lib/format';

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const event = useEvent(id);
  const update = useUpdateEvent(id ?? '');
  const remove = useDeleteEvent();

  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const canEdit =
    !!user && !!event.data &&
    (event.data.createdByUserId === user.id || isAdmin);

  if (event.isLoading) {
    return <p className="px-4 py-12 text-center text-sm text-slate-400">Lädt …</p>;
  }
  if (event.error || !event.data) {
    return (
      <p className="px-4 py-12 text-center text-sm text-rose-400">
        Event nicht gefunden
      </p>
    );
  }

  const e = event.data;

  return (
    <div className="px-4 py-4 space-y-4">
      {!editing ? (
        <>
          <header className="space-y-1">
            <p className="text-xs uppercase tracking-wide text-slate-500">
              {e.endAt
                ? `${formatDate(e.startAt)} – ${formatDate(e.endAt)}`
                : formatDateTime(e.startAt)}
            </p>
            <h1 className="text-2xl font-semibold">{e.name}</h1>
            <p className="text-sm text-slate-400">{e.location}</p>
            {e.createdBy && (
              <p className="text-xs text-slate-500">
                Erstellt von {e.createdBy.firstName} {e.createdBy.lastName}
              </p>
            )}
          </header>

          {e.description && (
            <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-sm whitespace-pre-wrap">
              {e.description}
            </section>
          )}

          {canEdit && (
            <div className="flex gap-2">
              <button
                onClick={() => setEditing(true)}
                className="flex-1 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm hover:bg-slate-700"
              >
                Bearbeiten
              </button>
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-200 hover:bg-rose-900/60"
                >
                  Löschen
                </button>
              ) : (
                <>
                  <button
                    onClick={() =>
                      remove.mutate(e.id, {
                        onSuccess: () =>
                          navigate('/app/events', { replace: true }),
                      })
                    }
                    disabled={remove.isPending}
                    className="rounded-lg bg-rose-700 px-3 py-2 text-sm text-white hover:bg-rose-600 disabled:opacity-60"
                  >
                    Wirklich löschen?
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="rounded-lg border border-slate-700 px-3 py-2 text-sm"
                  >
                    Abbrechen
                  </button>
                </>
              )}
            </div>
          )}
          {remove.error instanceof ApiError && (
            <p className="text-xs text-rose-400">{remove.error.message}</p>
          )}
        </>
      ) : (
        <EditForm
          event={e}
          onSave={(input) =>
            update.mutate(input, { onSuccess: () => setEditing(false) })
          }
          isSaving={update.isPending}
          apiError={
            update.error instanceof ApiError ? update.error.message : null
          }
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
}

function EditForm({
  event,
  onSave,
  isSaving,
  apiError,
  onCancel,
}: {
  event: { name: string; startAt: string; endAt: string | null; location: string; description: string | null };
  onSave: (input: UpdateEventInput) => void;
  isSaving: boolean;
  apiError: string | null;
  onCancel: () => void;
}) {
  const [name, setName] = useState(event.name);
  const [startAt, setStartAt] = useState(toDateTimeLocal(event.startAt));
  const [hasEnd, setHasEnd] = useState(!!event.endAt);
  const [endAt, setEndAt] = useState(
    event.endAt ? toDateTimeLocal(event.endAt) : toDateTimeLocal(event.startAt),
  );
  const [location, setLocation] = useState(event.location);
  const [description, setDescription] = useState(event.description ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const input: UpdateEventInput = {
      name: name.trim(),
      startAt: fromDateTimeLocal(startAt),
      endAt: hasEnd ? fromDateTimeLocal(endAt) : null,
      location: location.trim(),
      description: description.trim() || null,
    };
    const parsed = updateEventSchema.safeParse(input);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const iss of parsed.error.issues) {
        const k = iss.path[0]?.toString();
        if (k && !errs[k]) errs[k] = iss.message;
      }
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    onSave(parsed.data);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <h2 className="text-lg font-semibold">Event bearbeiten</h2>

      <FieldLocal label="Name" error={fieldErrors['name']}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
        />
      </FieldLocal>

      <FieldLocal label="Startdatum + Uhrzeit" error={fieldErrors['startAt']}>
        <input
          type="datetime-local"
          value={startAt}
          onChange={(e) => setStartAt(e.target.value)}
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
        />
      </FieldLocal>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={hasEnd}
            onChange={(e) => setHasEnd(e.target.checked)}
            className="h-4 w-4 rounded border-slate-600 bg-slate-950 accent-brand-600"
          />
          Enddatum / -uhrzeit angeben
        </label>
        {hasEnd && (
          <FieldLocal label="Ende" error={fieldErrors['endAt']}>
            <input
              type="datetime-local"
              value={endAt}
              onChange={(e) => setEndAt(e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
            />
          </FieldLocal>
        )}
      </div>

      <FieldLocal label="Ort" error={fieldErrors['location']}>
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
        />
      </FieldLocal>

      <FieldLocal label="Beschreibung (optional)" error={fieldErrors['description']}>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
        />
      </FieldLocal>

      {apiError && (
        <div className="rounded-lg border border-rose-900 bg-rose-950/60 px-3 py-2 text-sm text-rose-200">
          {apiError}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSaving}
          className="flex-1 rounded-lg bg-brand-600 px-4 py-2.5 font-medium text-white hover:bg-brand-700 disabled:opacity-60"
        >
          {isSaving ? 'Speichern …' : 'Speichern'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-slate-700 px-4 py-2.5 text-sm"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function FieldLocal({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-400">{label}</label>
      {children}
      {error && <p className="text-xs text-rose-400">{error}</p>}
    </div>
  );
}
